<template>
  <ClientOnly>
    <div class="establishment-search-map">
      <div
        class="establishment-search-map__map-shell"
        :class="{ 'establishment-search-map__map-shell--loading': props.loading }">
        <div
          ref="mapEl"
          class="establishment-search-map__canvas" />
      </div>
      <Transition name="establishment-search-map__pill-fade">
        <div
          v-if="props.loading && props.searchingLabel"
          class="establishment-search-map__loading-pill"
          role="status"
          aria-live="polite">
          <span
            class="establishment-search-map__spinner"
            aria-hidden="true" />
          <span>{{ props.searchingLabel }}</span>
        </div>
      </Transition>
      <div class="establishment-search-map__controls">
        <label class="establishment-search-map__check">
          <input
            type="checkbox"
            :checked="props.mapSearchEnabled"
            @change="onToggleMapSearch" />
          <span>{{ props.mapSearchLabel }}</span>
        </label>
        <div class="establishment-search-map__apply-wrap">
          <!-- Bouton natif + classes design system : même rendu que PrePeersButton (évite soucis d'affichage dans ClientOnly / Leaflet). -->
          <button
            type="button"
            class="prepeers-button btn btn-primary btn-sm establishment-search-map__apply"
            :disabled="!props.mapSearchEnabled"
            @click="onApplyBounds">
            {{ props.applyBoundsLabel }}
          </button>
        </div>
      </div>
    </div>
    <template #fallback>
      <div class="establishment-search-map establishment-search-map--fallback">
        <p>{{ props.loadingLabel }}</p>
      </div>
    </template>
  </ClientOnly>
</template>

<script setup lang="ts">
import {
  createVNode,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  render,
  shallowRef,
  watch,
} from 'vue'
import type {
  LeafletEvent,
  Map as LeafletMap,
  Marker as LeafletMarker,
  MarkerCluster,
  MarkerClusterGroup,
  TooltipOptions,
} from 'leaflet'
import type { PropType, Ref, ShallowRef } from 'vue'

import type {
  EstablishmentSearchMapMarker,
  EstablishmentSearchMapProps,
} from '~/types/EstablishmentSearchMap'
import type { EstablishmentSearchBBox } from '~/types/EstablishmentSearch'
import { configureLeafletDefaultIcons } from '~/utils/leafletIcons'
import EstablishmentMapTooltip from '~/components/search/EstablishmentMapTooltip.vue'

/**
 * Définit les props du composant EstablishmentSearchMap.
 */
const props: EstablishmentSearchMapProps = defineProps({
  markers: {
    type: Array as PropType<EstablishmentSearchMapMarker[]>,
    required: true,
  },
  selectedId: {
    type: String as PropType<string | null>,
    default: null,
  },
  /** Carte survolée à gauche : son marqueur (ou le cluster qui le contient) rebondit tant que le survol dure. */
  hoveredId: {
    type: String as PropType<string | null>,
    default: null,
  },
  /** Centre initial (ex. ville étudiante proche). Une seule fois au montage si défini. */
  initialCenter: {
    type: Object as PropType<{ lat: number; lng: number } | null>,
    default: null,
  },
  /** Zone initiale à cadrer via `fitBounds` (prioritaire sur `initialCenter`). */
  initialBounds: {
    type: Object as PropType<EstablishmentSearchBBox | null>,
    default: null,
  },
  initialZoom: {
    type: Number,
    default: 6,
  },
  mapSearchEnabled: {
    type: Boolean,
    required: true,
  },
  mapSearchLabel: {
    type: String,
    required: true,
  },
  applyBoundsLabel: {
    type: String,
    required: true,
  },
  loadingLabel: {
    type: String,
    required: true,
  },
  /** Recherche en cours (refetch résultats) : affiche la pastille de chargement sur la carte. */
  loading: {
    type: Boolean,
    default: false,
  },
  /** Libellé de la pastille « les résultats arrivent » (distinct du chargement Leaflet). */
  searchingLabel: {
    type: String,
    default: '',
  },
  /** Formate le « et N de plus » du tooltip cluster (i18n résolu côté parent). */
  formatMoreCount: {
    type: Function as PropType<(count: number) => string>,
    default: (count: number): string => `+${count}`,
  },
})

const emit = defineEmits<{
  'update:mapSearch': [value: boolean]
  'apply-bounds': [bbox: EstablishmentSearchBBox]
  'bounds-change': [bbox: EstablishmentSearchBBox, shouldFetch: boolean]
  'select-marker': [id: string]
}>()

const MAP_BOUNDS_DEBOUNCE_MS = 300

let boundsDebounceTimer: ReturnType<typeof setTimeout> | null = null
const viewportAdjustedByUser: Ref<boolean> = ref(false)
/** Ignore les `moveend` / `zoomend` déclenchés par `flyTo`, `fitBounds`, etc. */
let suppressViewportSearchCount = 0

/** Élément Leaflet (marqueur ou cluster) qui rebondit actuellement, pour retirer la classe au survol suivant. */
let bouncingEl: HTMLElement | null = null

const mapEl: Ref<HTMLElement | null> = ref<HTMLElement | null>(null)

const initialMapNull: LeafletMap | null = null
const map: ShallowRef<LeafletMap | null> = shallowRef(initialMapNull)

const initialClusterNull: MarkerClusterGroup | null = null
const markerCluster: ShallowRef<MarkerClusterGroup | null> = shallowRef(initialClusterNull)

const initialMarkersMap: Map<string, LeafletMarker> = new Map()
const leafletMarkers: ShallowRef<Map<string, LeafletMarker>> = shallowRef(initialMarkersMap)

/** Données par marqueur Leaflet : reconstituent le contenu d'un cluster au survol. */
const markerData: WeakMap<LeafletMarker, EstablishmentSearchMapMarker> = new WeakMap<
  LeafletMarker,
  EstablishmentSearchMapMarker
>()

/** `direction: 'auto'` : Leaflet ouvre le tooltip du côté qui reste dans la carte (évite le rognage en bord). */
const TOOLTIP_OPTIONS: TooltipOptions = {
  direction: 'auto',
  className: 'establishment-map-tooltip',
}

/**
 * @param {Event} event - Changement checkbox.
 * @returns {void}
 */
/**
 * @returns {EstablishmentSearchBBox | null} Bbox Leaflet courante ou `null`.
 */
function readCurrentBBox(): EstablishmentSearchBBox | null {
  if (!map.value) return null
  const b = map.value.getBounds()
  // Dézoomé, Leaflet renvoie des lng/lat hors limites (au-delà de ±180 / ±90) qui font planter
  // l'API (500). On borne aux plages géographiques valides.
  const clampLat = (v: number): number => Math.max(-90, Math.min(90, v))
  const clampLng = (v: number): number => Math.max(-180, Math.min(180, v))
  return {
    southWestLat: clampLat(b.getSouthWest().lat),
    southWestLng: clampLng(b.getSouthWest().lng),
    northEastLat: clampLat(b.getNorthEast().lat),
    northEastLng: clampLng(b.getNorthEast().lng),
  }
}

/**
 * @param {boolean} shouldFetch - Déclencher un appel API côté hub.
 * @returns {void}
 */
function emitBoundsChange(shouldFetch: boolean): void {
  const bbox: EstablishmentSearchBBox | null = readCurrentBBox()
  if (!bbox) return
  emit('bounds-change', bbox, shouldFetch)
}

/**
 * @param {Event} event - Changement checkbox.
 * @returns {void}
 */
function onToggleMapSearch(event: Event): void {
  const checked: boolean = (event.target as HTMLInputElement).checked
  emit('update:mapSearch', checked)
  if (checked) {
    emitBoundsChange(true)
  }
}

/**
 * @returns {void}
 */
function markViewportAdjustedByUser(): void {
  if (suppressViewportSearchCount > 0) return
  viewportAdjustedByUser.value = true
}

/**
 * Exécute un déplacement programmatique sans relancer la recherche carte.
 * @param {() => void} action - Appel Leaflet (`flyTo`, `fitBounds`, …).
 * @returns {void}
 */
function runWithoutViewportSearch(action: () => void): void {
  suppressViewportSearchCount++
  viewportAdjustedByUser.value = false
  action()
}

/**
 * Pan / zoom : refetch seulement si « rechercher sur la carte » est actif.
 * @returns {void}
 */
function onMapViewportSettled(): void {
  // Seuls les déplacements initiés par l'utilisateur (drag/zoom) relancent une recherche.
  // Un recadrage programmatique (fitBounds sur les résultats, flyTo) ne doit PAS refetch, sinon
  // résultats → fitBounds → moveend → refetch → … boucle infinie.
  if (suppressViewportSearchCount > 0) {
    suppressViewportSearchCount--
    viewportAdjustedByUser.value = false
    emitBoundsChange(false)
    return
  }

  const userMove: boolean = viewportAdjustedByUser.value
  viewportAdjustedByUser.value = false

  if (userMove && props.mapSearchEnabled) {
    if (boundsDebounceTimer !== null) clearTimeout(boundsDebounceTimer)
    boundsDebounceTimer = setTimeout((): void => {
      emitBoundsChange(true)
    }, MAP_BOUNDS_DEBOUNCE_MS)
    return
  }
  emitBoundsChange(false)
}

/**
 * Pousse la bbox courante de la carte vers le parent (filtre liste).
 * @returns {void}
 */
function onApplyBounds(): void {
  if (!map.value) return
  const b = map.value.getBounds()
  const bbox: EstablishmentSearchBBox = {
    southWestLat: b.getSouthWest().lat,
    southWestLng: b.getSouthWest().lng,
    northEastLat: b.getNorthEast().lat,
    northEastLng: b.getNorthEast().lng,
  }
  emit('apply-bounds', bbox)
}

/**
 * @returns {Promise<void>}
 */
async function initMap(): Promise<void> {
  if (!mapEl.value || map.value) return
  await loadLeafletStyles()
  const L = (await import('leaflet')).default
  await import('leaflet.markercluster')

  configureLeafletDefaultIcons(L)

  const center: [number, number] = props.initialCenter
    ? [props.initialCenter.lat, props.initialCenter.lng]
    : [46.603354, 1.888334]
  const zoom: number = props.initialCenter ? 11 : props.initialZoom

  map.value = L.map(mapEl.value, { scrollWheelZoom: true, zoomControl: true }).setView(center, zoom)

  if (props.initialBounds) {
    map.value.fitBounds([
      [props.initialBounds.southWestLat, props.initialBounds.southWestLng],
      [props.initialBounds.northEastLat, props.initialBounds.northEastLng],
    ])
  }

  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '© OpenStreetMap contributors',
    maxZoom: 19,
  }).addTo(map.value)

  markerCluster.value = L.markerClusterGroup({
    showCoverageOnHover: false,
    spiderfyOnMaxZoom: true,
    zoomToBoundsOnClick: true,
    maxClusterRadius: 60,
  })
  map.value.addLayer(markerCluster.value)
  markerCluster.value.on('clustermouseover', onClusterMouseOver)
  map.value.on('dragstart', markViewportAdjustedByUser)
  map.value.on('zoomstart', markViewportAdjustedByUser)
  map.value.getContainer().addEventListener('wheel', markViewportAdjustedByUser, { passive: true })
  map.value.on('moveend', onMapViewportSettled)
  map.value.on('zoomend', onMapViewportSettled)

  if (props.mapSearchEnabled) {
    emitBoundsChange(true)
  }

  setTimeout((): void => {
    map.value?.invalidateSize()
  }, 100)
  setTimeout((): void => {
    map.value?.invalidateSize()
  }, 400)
}

/**
 * Rend le composant tooltip dans un élément détaché pour Leaflet (`bindTooltip` attend du DOM).
 * @param {EstablishmentSearchMapMarker[]} tooltipMarkers - Entité(s) au point survolé.
 * @returns {HTMLElement} Élément racine du tooltip rendu.
 */
function renderTooltip(tooltipMarkers: EstablishmentSearchMapMarker[]): HTMLElement {
  const container: HTMLDivElement = document.createElement('div')
  render(
    createVNode(EstablishmentMapTooltip, {
      markers: tooltipMarkers,
      formatMoreCount: props.formatMoreCount,
    }),
    container,
  )
  return (container.firstElementChild as HTMLElement | null) ?? container
}

/**
 * Survol d'un cluster : ouvre un tooltip listant les établissements/programmes regroupés.
 * @param {LeafletEvent} event - Événement `clustermouseover` (sa `layer` est le cluster).
 * @returns {void}
 */
function onClusterMouseOver(event: LeafletEvent): void {
  const cluster: MarkerCluster = (event as LeafletEvent & { layer: MarkerCluster }).layer
  const clusteredMarkers: EstablishmentSearchMapMarker[] = cluster
    .getAllChildMarkers()
    .map((child: LeafletMarker): EstablishmentSearchMapMarker | undefined => markerData.get(child))
    .filter(
      (marker: EstablishmentSearchMapMarker | undefined): marker is EstablishmentSearchMapMarker =>
        marker !== undefined,
    )
  cluster.bindTooltip(renderTooltip(clusteredMarkers), TOOLTIP_OPTIONS).openTooltip()
}

/**
 * @returns {Promise<void>}
 */
async function renderMarkers(): Promise<void> {
  if (!map.value || !markerCluster.value) return
  const L = (await import('leaflet')).default
  markerCluster.value.clearLayers()
  const nextMarkers: Map<string, LeafletMarker> = new Map()
  const layers: LeafletMarker[] = []
  for (const m of props.markers) {
    const marker = L.marker([m.lat, m.lng])
    marker.bindTooltip((): HTMLElement => renderTooltip([m]), TOOLTIP_OPTIONS)
    marker.on('click', (): void => {
      emit('select-marker', m.id)
    })
    markerData.set(marker, m)
    nextMarkers.set(m.id, marker)
    layers.push(marker)
  }
  leafletMarkers.value = nextMarkers
  markerCluster.value.addLayers(layers)
  await nextTick()
  map.value.invalidateSize()
  // Marqueurs reconstruits : ré-applique le rebond si une carte est encore survolée.
  syncHoverBounce(props.hoveredId)
}

/**
 * @param {string | null} id - Identifiant sélectionné.
 * @returns {Promise<void>}
 */
async function syncSelectionHighlight(id: string | null): Promise<void> {
  if (!map.value) return
  for (const [mid, marker] of leafletMarkers.value.entries()) {
    const el = marker.getElement()
    if (!el) continue
    if (id != null && mid === id) {
      el.classList.add('establishment-search-map-marker--selected')
    } else {
      el.classList.remove('establishment-search-map-marker--selected')
    }
  }
}

/**
 * Fait rebondir le marqueur survolé à gauche (ou le cluster qui le contient s'il est regroupé),
 * tant que `id` est défini. Un seul élément rebondit à la fois.
 * @param {string | null} id - Id de la carte survolée, ou `null` pour stopper le rebond.
 * @returns {void}
 */
function syncHoverBounce(id: string | null): void {
  if (bouncingEl) {
    bouncingEl.classList.remove('establishment-search-map-marker--bounce')
    bouncingEl = null
  }
  if (id == null || !markerCluster.value) return
  const marker: LeafletMarker | undefined = leafletMarkers.value.get(id)
  if (!marker) return
  // getVisibleParent : le marqueur lui-même s'il est visible, sinon le cluster qui le contient.
  const visible: LeafletMarker | null = markerCluster.value.getVisibleParent(marker)
  const el: HTMLElement | undefined = visible?.getElement()
  if (!el) return
  el.classList.add('establishment-search-map-marker--bounce')
  bouncingEl = el
}

watch(
  (): { lat: number; lng: number } | null => props.initialCenter,
  (c: { lat: number; lng: number } | null): void => {
    if (!c || !map.value) return
    runWithoutViewportSearch((): void => {
      map.value?.setView([c.lat, c.lng], 11)
    })
  },
)

watch(
  (): EstablishmentSearchBBox | null => props.initialBounds ?? null,
  (b: EstablishmentSearchBBox | null): void => {
    if (!b || !map.value) return
    runWithoutViewportSearch((): void => {
      map.value?.fitBounds([
        [b.southWestLat, b.southWestLng],
        [b.northEastLat, b.northEastLng],
      ])
    })
  },
)

watch(
  (): string =>
    props.markers.map((m: EstablishmentSearchMapMarker) => `${m.id}:${m.lat},${m.lng}`).join('|'),
  (): void => {
    void renderMarkers()
  },
)

watch(
  (): string | null => props.selectedId,
  (id: string | null): void => {
    void syncSelectionHighlight(id)
    if (!id || !map.value) return
    const hit = props.markers.find((m: EstablishmentSearchMapMarker) => m.id === id)
    if (!hit) return
    runWithoutViewportSearch((): void => {
      map.value?.flyTo([hit.lat, hit.lng], Math.max(map.value?.getZoom() ?? 13, 13), {
        duration: 0.6,
      })
    })
  },
)

watch(
  (): string | null => props.hoveredId,
  (id: string | null): void => {
    syncHoverBounce(id)
  },
)

onMounted((): void => {
  const stop = watch(
    mapEl,
    (el: HTMLElement | null): void => {
      if (!el) return
      stop()
      void (async (): Promise<void> => {
        await initMap()
        await renderMarkers()
      })()
    },
    { immediate: true, flush: 'post' },
  )
})

onBeforeUnmount((): void => {
  if (boundsDebounceTimer !== null) clearTimeout(boundsDebounceTimer)
  map.value?.getContainer().removeEventListener('wheel', markViewportAdjustedByUser)
  map.value?.remove()
  map.value = initialMapNull
  markerCluster.value = initialClusterNull
  leafletMarkers.value = new Map<string, LeafletMarker>()
})
</script>

<style scoped>
.establishment-search-map {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  min-width: 0;
  border-radius: var(--radius-md);
  /* overflow visible : les contrôles (checkbox + bouton) ne doivent pas être rognés par `overflow: hidden`. */
  overflow: visible;
  border: 1px solid var(--border-default);
  background: var(--bg-elevated);
}

/* Tuiles Leaflet seulement : coins arrondis + clip sans masquer les contrôles absolus au-dessus. */
.establishment-search-map__map-shell {
  position: absolute;
  inset: 0;
  z-index: 0;
  border-radius: inherit;
  overflow: hidden;
}

.establishment-search-map__canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

.establishment-search-map :deep(.leaflet-container) {
  width: 100%;
  height: 100%;
  min-height: inherit;
  font: inherit;
  border-radius: inherit;
}

.establishment-search-map__controls {
  position: absolute;
  top: var(--space-3);
  right: var(--space-3);
  z-index: 500;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--space-2);
  max-width: min(260px, 85%);
}

.establishment-search-map__check {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  background: rgba(255, 255, 255, 0.97);
  border: 1px solid rgba(7, 7, 15, 0.12);
  font-size: var(--text-xs);
  /* Fond clair : le thème utilise --text-primary en blanc — texte lisible sur pastille carte */
  color: var(--text-inverse);
  cursor: pointer;
  box-shadow: var(--shadow-sm);
}

.establishment-search-map__check input[type='checkbox'] {
  accent-color: var(--accent-blue);
}

.establishment-search-map__apply-wrap {
  width: 100%;
  align-self: stretch;
}

/* CTA zone carte : mêmes classes globales que PrePeersButton (primary + sm). */
.establishment-search-map__apply-wrap .establishment-search-map__apply {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  justify-content: center;
  text-align: center;
  white-space: normal;
}

.establishment-search-map--fallback {
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  font-size: var(--text-sm);
}

/* Recherche en cours : on atténue les anciens marqueurs/tuiles sans bloquer l'interaction. */
.establishment-search-map__map-shell--loading :deep(.leaflet-marker-pane),
.establishment-search-map__map-shell--loading :deep(.leaflet-marker-icon) {
  opacity: 0.45;
  transition: opacity 0.2s ease;
}

/* Pastille flottante haut-centre : signale que les résultats arrivent, non bloquante. */
.establishment-search-map__loading-pill {
  position: absolute;
  top: var(--space-3);
  left: 50%;
  transform: translateX(-50%);
  z-index: 600;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-full, 999px);
  background: rgba(255, 255, 255, 0.97);
  border: 1px solid rgba(7, 7, 15, 0.12);
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--text-inverse);
  box-shadow: var(--shadow-sm);
  pointer-events: none;
  max-width: min(280px, 80%);
}

.establishment-search-map__spinner {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 2px solid rgba(7, 7, 15, 0.18);
  border-top-color: var(--accent-blue);
  animation: establishment-search-map-spin 0.7s linear infinite;
}

@keyframes establishment-search-map-spin {
  to {
    transform: rotate(360deg);
  }
}

.establishment-search-map__pill-fade-enter-active,
.establishment-search-map__pill-fade-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.establishment-search-map__pill-fade-enter-from,
.establishment-search-map__pill-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, -6px);
}

@media (prefers-reduced-motion: reduce) {
  .establishment-search-map__spinner {
    animation-duration: 1.4s;
  }
}
</style>

<style>
/* sélection marqueur (classe sur élément Leaflet) */
.establishment-search-map-marker--selected {
  filter: drop-shadow(0 0 6px rgba(249, 115, 22, 0.95));
}

.establishment-search-map-marker--bounce {
  animation: establishment-search-map-bounce 0.65s ease-in-out infinite;
}

@keyframes establishment-search-map-bounce {
  0%,
  100% {
    translate: 0 0;
  }
  50% {
    translate: 0 -22px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .establishment-search-map-marker--bounce {
    animation: none;
  }
}

/* Le visuel du tooltip vit dans le composant EstablishmentMapTooltip (style scopé). Ici on neutralise
   seulement la coquille par défaut de Leaflet (fond, bordure, flèche) pour ne garder que la mini-carte. */
.leaflet-tooltip.establishment-map-tooltip {
  padding: 0;
  border: none;
  background: none;
  box-shadow: none;
  white-space: normal;
}

.leaflet-tooltip.establishment-map-tooltip::before {
  display: none;
}
</style>
